/**
 * Where the points live until they are exported: inside the phone, and nowhere
 * else.
 *
 * IndexedDB and not `localStorage` for one reason that decides it: photos are
 * binary and `localStorage` only holds text, so a photo would have to be turned
 * into base64 — a third bigger, and every read and write copying megabytes of
 * string. IndexedDB stores the blob as it is.
 *
 * Nothing here talks to the network. There is no network code in this app at
 * all, and that is the strongest privacy guarantee available: a day's walking
 * cannot leak from a device it never leaves.
 */
import type { StoredPoint } from "./types.js";

const DB_NAME = "avisos-camino";
const STORE = "points";
const DB_VERSION = 1;

let db: IDBDatabase | null = null;

/** Wraps a request in a promise, keeping the failure reason. */
function ask<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = (): void => resolve(request.result);
    request.onerror = (): void => reject(request.error ?? new Error("IndexedDB falló"));
  });
}

async function open(): Promise<IDBDatabase> {
  if (db !== null) return db;
  const request = indexedDB.open(DB_NAME, DB_VERSION);
  request.onupgradeneeded = (): void => {
    const created = request.result;
    if (!created.objectStoreNames.contains(STORE)) {
      created.createObjectStore(STORE, { keyPath: "id" });
    }
  };
  db = await ask(request);
  return db;
}

/**
 * Waits for a transaction to actually commit.
 *
 * The listeners have to be attached **before** anything else is awaited. A
 * transaction commits by itself as soon as the browser runs out of work for
 * it, and that can happen in the same tick: attaching `oncomplete` after an
 * `await` means attaching it to an event that has already fired, and the app
 * then waits for ever on a write that in fact succeeded.
 *
 * This is not hypothetical. Written the other way round, the first point saved
 * fine and the second one hung the app silently, with nothing in the console.
 */
function committed(tx: IDBTransaction, whatFailed: string): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = (): void => resolve();
    tx.onerror = (): void => reject(tx.error ?? new Error(whatFailed));
    tx.onabort = (): void => reject(tx.error ?? new Error(whatFailed));
  });
}

async function write<T>(run: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const database = await open();
  const tx = database.transaction(STORE, "readwrite");
  const done = committed(tx, "No se pudo guardar");
  const result = await ask(run(tx.objectStore(STORE)));
  // Waiting for the commit, and not just for the request: a request that
  // resolves only means the write was queued. On a phone that gets locked
  // mid-export, the difference is a point that looks saved and is not.
  await done;
  return result;
}

/** Adds a point, or replaces it if it was already there. */
export async function savePoint(point: StoredPoint): Promise<void> {
  await write((store) => store.put(point));
}

/**
 * Fills in anything a record written by an older build might be missing.
 *
 * Belt and braces rather than a real migration: this app had not been
 * published when a point stopped having a single photo and started answering
 * its tag's questions, so in practice there is nothing out there in the old
 * shape. It costs a few lines, and the alternative — a phone full of a walk
 * that will not open — is not worth saving them.
 *
 * The same goes for a photo stored as a bare `Blob`, from before a photo
 * carried what it was a photo of: read back as `kind: "general"`, which is
 * exactly what it already was.
 */
function normalise(row: StoredPoint): StoredPoint {
  const photos = Array.isArray(row.photos) ? row.photos : [];
  return {
    ...row,
    photos: photos.map((photo) =>
      photo instanceof Blob ? { blob: photo, kind: "general" } : photo,
    ),
    fields: typeof row.fields === "object" && row.fields !== null ? row.fields : {},
  };
}

/** Everything captured, oldest first. */
export async function allPoints(): Promise<StoredPoint[]> {
  const database = await open();
  const tx = database.transaction(STORE, "readonly");
  const rows = await ask(tx.objectStore(STORE).getAll() as IDBRequest<StoredPoint[]>);
  return rows.map(normalise).sort((a, b) => a.id.localeCompare(b.id));
}

export async function deletePoint(id: string): Promise<void> {
  await write((store) => store.delete(id));
}

/**
 * Marks points as already exported.
 *
 * They are not deleted, on purpose. An export that ends up on a laptop that
 * then dies is not a backup, and the phone is the only copy until somebody
 * says otherwise. Deleting is a separate, deliberate act.
 */
export async function markExported(ids: readonly string[]): Promise<void> {
  const wanted = new Set(ids);
  const database = await open();
  const tx = database.transaction(STORE, "readwrite");
  const done = committed(tx, "No se pudo marcar lo exportado");
  // Walked with a cursor rather than a get-then-put per id, because awaiting
  // in the middle of a transaction lets it commit underneath you and the next
  // write then fails. Everything here happens inside the browser's own events.
  const walk = tx.objectStore(STORE).openCursor();
  walk.onsuccess = (): void => {
    const cursor = walk.result;
    if (cursor === null) return;
    const point = cursor.value as StoredPoint;
    if (wanted.has(point.id) && !point.exported) cursor.update({ ...point, exported: true });
    cursor.continue();
  };
  await done;
}

/**
 * Removes several points in one go.
 *
 * One transaction rather than one per point, so a phone that gets locked
 * halfway through does not leave half a day deleted and half not. Walked with
 * a cursor for the same reason `markExported` is: awaiting in the middle of a
 * transaction lets it commit underneath you.
 */
export async function deleteMany(ids: readonly string[]): Promise<void> {
  const wanted = new Set(ids);
  if (wanted.size === 0) return;
  const database = await open();
  const tx = database.transaction(STORE, "readwrite");
  const done = committed(tx, "No se pudo borrar");
  const walk = tx.objectStore(STORE).openCursor();
  walk.onsuccess = (): void => {
    const cursor = walk.result;
    if (cursor === null) return;
    if (wanted.has((cursor.value as StoredPoint).id)) cursor.delete();
    cursor.continue();
  };
  await done;
}
